// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useEffect, useState, useContext } from 'react'
import { PropTypes } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box } from 'grommet'
import lodashGet from 'lodash/get'

import { FormInput } from '../../../../../../components'
import { WizardContext } from '../../../../../../components/wizard/WizardContext'

import ValidateMetadataURL from './add-idp-details/components/OIDCMetadataURLValidation'
import { defaultFieldsError } from './utils/page-utils'

const SamlOIDCConfiguration = ({ onIssuerUrlChange, profileId }) => {
  const { t } = useTranslation(['common', 'iam', 'authn'])
  const [metadataEnabled, setMetadataEnabled] = useState(true)
  const { formValues, setFormValues, attemptedAdvance, setAttemptedAdvance } =
    useContext(WizardContext)
  const [errorMessage, setErrorMessage] = useState({
    issuer: '',
    authorizationEndpoint: '',
    tokenEndpoint: '',
    jwksEndpoint: '',
    userInfoEndpoint: ''
  })
  const [fieldsError, setFieldsError] = useState(defaultFieldsError)

  useEffect(() => {
    if (profileId) {
      // Only execute if editing (profileId exists)
      const existingOIDCValues = formValues.oidcIdpConfig
      if (existingOIDCValues.issuer) {
        setMetadataEnabled(false) // Switch to manual mode if `issuer` is present
      }
    }
  }, [profileId, formValues.oidcIdpConfig, formValues])

  // Validation logic on attempted advance
  useEffect(() => {
    if (attemptedAdvance) {
      if (metadataEnabled) {
        // Validate Metadata URL
        if (!lodashGet(formValues, 'oidcIdpConfig.metadata_url', '').trim()) {
          setFieldsError({
            ...fieldsError,
            metadataUrl: 'Metadata URL is required.'
          })
          setAttemptedAdvance(false)
        }
      } else {
        // Validate Manual Fields
        const requiredFields = [
          'issuer',
          'authorizationEndpoint',
          'tokenEndpoint',
          'jwksEndpoint',
          'userInfoEndpoint'
        ]
        const newErrors = requiredFields.reduce((acc, field) => {
          if (!lodashGet(formValues, `oidcIdpConfig.${field}`, '').trim()) {
            acc[field] = 'This field is required.'
          }
          return acc
        }, {})
        setErrorMessage(newErrors)

        if (Object.keys(newErrors).length > 0) {
          setAttemptedAdvance(false)
        }
      }
    }
  }, [
    attemptedAdvance,
    metadataEnabled,
    formValues,
    setAttemptedAdvance,
    fieldsError,
    t
  ])

  return (
    <Box fill align="left" justify="start">
      <Box direction="column" testId="oidcconfig_inputs">
        <ValidateMetadataURL
          metadataEnabled={metadataEnabled}
          setMetadataEnabled={setMetadataEnabled}
          setIssuerUrl={onIssuerUrlChange}
        />
        {!metadataEnabled && (
          <>
            <FormInput
              required
              label={t('iam:sso_profiles_oidc.issuer_url_label')}
              labelHelper={t('iam:sso_profiles_oidc.issuer_url_description')}
              inputType="text"
              size="small"
              margin={{ bottom: 'small' }}
              name="issuerUrl-endpoint"
              testId="issuerUrl-endpoint-input"
              width="large"
              value={lodashGet(formValues, 'oidcIdpConfig.issuer', '')}
              onChange={(event) => {
                const updatedUrl = event.target.value.trim()
                setFormValues({
                  ...formValues,
                  oidcIdpConfig: {
                    ...formValues.oidcIdpConfig,
                    issuer: updatedUrl
                  }
                })

                // Notify parent about the change
                if (onIssuerUrlChange) {
                  onIssuerUrlChange(updatedUrl)
                }
              }}
              error={errorMessage.issuer}
            />
            <FormInput
              required
              label={t('iam:sso_profiles_oidc.authorization_endpoint_label')}
              labelHelper={t(
                'iam:sso_profiles_oidc.authorization_endpoint_description'
              )}
              inputType="text"
              size="small"
              margin={{ bottom: 'small' }}
              name="authorization-endpoint"
              testId="authorization-endpoint-input"
              width="large"
              value={lodashGet(
                formValues,
                'oidcIdpConfig.authorizationEndpoint',
                ''
              )}
              onChange={(event) => {
                setFormValues({
                  ...formValues,
                  oidcIdpConfig: {
                    ...formValues.oidcIdpConfig,
                    authorizationEndpoint: event.target.value.trim()
                  }
                })
              }}
              error={errorMessage.authorizationEndpoint}
            />
            <FormInput
              required
              label={t('iam:sso_profiles_oidc.token_endpoint_label')}
              labelHelper={t(
                'iam:sso_profiles_oidc.token_endpoint_description'
              )}
              inputType="text"
              margin={{ bottom: 'small' }}
              name="token-endpoint"
              testId="token-endpoint-input"
              width="large"
              value={lodashGet(formValues, 'oidcIdpConfig.tokenEndpoint', '')}
              onChange={(event) => {
                setFormValues({
                  ...formValues,
                  oidcIdpConfig: {
                    ...formValues.oidcIdpConfig,
                    tokenEndpoint: event.target.value.trim()
                  }
                })
              }}
              error={errorMessage.tokenEndpoint}
            />
            <FormInput
              required
              label={t('iam:sso_profiles_oidc.jwks_endpoint_label')}
              labelHelper={t('iam:sso_profiles_oidc.jwks_endpoint_description')}
              inputType="text"
              margin={{ bottom: 'small' }}
              name="jwks-endpoint"
              testId="jwks-endpoint-input"
              width="large"
              value={lodashGet(formValues, 'oidcIdpConfig.jwksEndpoint', '')}
              onChange={(event) => {
                setFormValues({
                  ...formValues,
                  oidcIdpConfig: {
                    ...formValues.oidcIdpConfig,
                    jwksEndpoint: event.target.value.trim()
                  }
                })
              }}
              error={errorMessage.jwksEndpoint}
            />
            <FormInput
              required
              label={t('iam:sso_profiles_oidc.userinfo_endpoint')}
              labelHelper={t(
                'iam:sso_profiles_oidc.userinfo_endpoint_description'
              )}
              inputType="text"
              margin={{ bottom: 'small' }}
              name="userinfo-endpoint"
              testId="userinfo-endpoint-input"
              width="large"
              value={lodashGet(
                formValues,
                'oidcIdpConfig.userInfoEndpoint',
                ''
              )}
              onChange={(event) => {
                setFormValues({
                  ...formValues,
                  oidcIdpConfig: {
                    ...formValues.oidcIdpConfig,
                    userInfoEndpoint: event.target.value.trim()
                  }
                })
              }}
              error={errorMessage.userInfoEndpoint}
            />
          </>
        )}
      </Box>
    </Box>
  )
}

SamlOIDCConfiguration.propTypes = {
  onIssuerUrlChange: PropTypes.func.isRequired,
  profileId: PropTypes.string
}

export default SamlOIDCConfiguration
